import * as Sentry from '@sentry/vue'

const PREFIX = 'checkout-perf'

/**
 * Человекочитаемые описания для каждой метрики.
 * Ключ — внутреннее имя (без префикса), значение — пояснение для консоли.
 */
const LABELS = {
  bootstrap:
    'От старта JS до конца app.mount(): DOM в дереве, но главный лоадер ещё висит — заполнять рано',
  'boot-app':
    'Подготовка приложения + GET /init (блокирующий запрос данных чекаута)',
  'init:translations': 'Загрузка переводов интерфейса',
  'init:request':
    'GET /init — запрос начальных данных чекаута (главный блокирующий запрос)',
  'init:post-process':
    'Обработка ответа /init: маски телефонов, символы, форматтеры',
  plugins: 'Инициализация плагинов Vue (i18n, маски, модалы и т.д.)',
  mount: 'Монтирование Vue-приложения в DOM (#appCheckout)',
  'checkout:mounted':
    'Доделки после монтирования (RU: /checkLocality → /order → /delivery); конец интервала = снятие лоадера (= checkout:ready)',
  'checkout:fields-config':
    'Чтение конфигурации полей формы (ФИО, телефон, email, адрес)',
  'checkout:order-data':
    'GET /order — загрузка данных заказа (состав, цены, скидки, кошелёк)',
  'checkout:locality-check':
    'GET /checkLocality — проверка доступности товаров для города [только RU]',
  'checkout:delivery-types':
    'GET /delivery + настройка способа доставки по умолчанию',
  'ui:first-field-ready':
    'Первое поле чекаута доступно для ввода (ФИО-шаг активен)',
  'ui:ident-ready':
    'Экран идентификации гостя (выбор членства) доступен для взаимодействия — точка раньше ФИО-шага, отделяет время загрузки от пользовательского выбора',
  'ui:price-visible': 'Блок с ценой заказа отобразился с реальными ценами',
  'ui:address-ready':
    'Блок адреса доступен для ввода (автокомплит города разблокирован)',
  'ui:delivery-types-visible': 'Список способов доставки отобразился',
  'ui:payment-visible': 'Блок оплаты отобразился',
  'checkout:ready':
    'Полная готовность чекаута — точка от открытия страницы (снятие главного лоадера)',
  'ui:wallet-visible': 'Баланс Coral Wallet отобразился (authed, CW_VALUE > 0)',
  'ui:profile-prefilled': 'Форма заполнена данными выбранного профиля',
  'ui:pickpoints-visible':
    'Выбор ПВЗ отобразился (список или карта с непустыми пунктами)',
  'promo:apply': 'Применение промокода — запрос + пересчёт цен',
  'checkout:pickpoints': 'GET /pickpoints — загрузка списка ПВЗ',
  'checkout:payments':
    'GET /payments — загрузка платёжных методов (если не пришли в /delivery)',
  'checkout:profiles': 'GET /profiles — загрузка списка профилей доставки',
  'checkout:order-create': 'POST /order — создание заказа (сетевой запрос)',
  'city:suggestions':
    'GET predictions Google Places — латентность API автокомплита (город)',
}

function describe(name) {
  return LABELS[name] || name
}

/**
 * Sentry принимает имена measurements только из [a-zA-Z0-9_] (точка разрешена),
 * имена с ':' и '-' Relay молча отбрасывает при приёме, поэтому перед отправкой
 * нормализуем их: init:request → init_request, boot-app → boot_app.
 */
function toSentryName(name) {
  return name.replace(/[^a-zA-Z0-9_]/g, '_')
}

/**
 * Нативная performance-разметка + интеграция с Sentry.
 *
 * Логи по умолчанию скрыты. Чтобы увидеть таймлайн, наберите в консоли:
 *   showPerf()        — сводная таблица всех замеров
 *
 * Sentry measurements отправляются всегда (в проде).
 *
 * timeline критического пути pageload:
 *
 *  bootstrap                                    ← DOM в дереве, лоадер ещё висит
 *    ├── boot-app  (включает init:request = GET /init)
 *    ├── plugins
 *    └── mount
 *         └── checkout:mounted                  ← конец интервала = снятие лоадера
 *              ├── checkout:fields-config
 *              ├── checkout:order-data      (GET /order)          [не-RU]
 *              ├── checkout:locality-check  (GET /checkLocality)  [RU]
 *              └── checkout:delivery-types  (GET /delivery + setup)
 *
 *  момент «можно заполнять форму» = точка checkout:ready (= конец checkout:mounted)
 *
 * Пользовательские фазы — ТОЧКИ от открытия страницы (perfPoint, не интервалы):
 *
 *  ui:first-field-ready        ← первое поле чекаута доступно для ввода
 *  ui:price-visible            ← блок с ценой заказа отобразился
 *  ui:address-ready            ← блок адреса доступен для ввода
 *  ui:delivery-types-visible   ← список способов доставки отобразился
 *  ui:payment-visible          ← блок оплаты отобразился
 */

/**
 * Точки пользовательских фаз, уже отправленные в Sentry (одноразовость на имя:
 * watchers и повторные загрузки данных не должны дублировать замер).
 */
const reportedPoints = new Set()

/**
 * Верхняя граница осмысленности точек, мс от открытия страницы: совпадает с
 * finalTimeout транзакции в src/plugins/sentry.js. Всё, что позже, — фоновые
 * и восстановленные вкладки, а не скорость загрузки.
 */
const POINT_MAX_MS = 30000

/**
 * Начало отсчёта интервала.
 */
export function perfStart(name) {
  const fullName = `${PREFIX}:${name}`
  if (typeof performance !== 'undefined' && performance.mark) {
    performance.mark(fullName + '.start')
  }
}

/**
 * Конец интервала: создаёт performance.measure,
 * отправляет measurement в Sentry (prod, если есть активная транзакция).
 *
 * @param {string} name — имя интервала (без префикса)
 * @param {{ report?: boolean }} [opts] — отключить отправку в Sentry
 * @returns {number} длительность в мс
 */
export function perfEnd(name, { report = true } = {}) {
  const fullName = `${PREFIX}:${name}`
  const startMark = fullName + '.start'
  const endMark = fullName + '.end'
  const measureName = fullName + '.measure'

  let duration = 0
  let measured = false

  if (
    typeof performance !== 'undefined' &&
    performance.mark &&
    performance.measure
  ) {
    performance.mark(endMark)
    try {
      performance.measure(measureName, startMark, endMark)
      const entries = performance.getEntriesByName(measureName)
      duration = entries[entries.length - 1]?.duration ?? 0
      measured = true
    } catch {
      // startMark отсутствует — пропускаем
    }
  }

  // нулевая duration валидна: микрозамеры (plugins, init:post-process) на
  // coarse-таймерах меряют 0; провал measure (нет start-mark) отсечён
  // флагом measured, а не duration
  if (report && window.prod && measured) {
    try {
      Sentry.setMeasurement(toSentryName(name), duration, 'millisecond')
    } catch {
      // нет активной транзакции — тихо игнорируем
    }
  }

  return duration
}

/**
 * Оборачивает функцию в измерение интервала. Основной API для чистой инструментации.
 *
 * @param {string} name
 * @param {() => T | Promise<T>} fn
 * @returns {T | Promise<T>}
 * @template T
 */
export function perfTrace(name, fn) {
  perfStart(name)
  try {
    const result = fn()
    if (result instanceof Promise) {
      return result.finally(() => perfEnd(name))
    }
    perfEnd(name)
    return result
  } catch (err) {
    perfEnd(name)
    throw err
  }
}

/**
 * Точка во времени от открытия страницы (performance.now() — мс от navigation start,
 * то есть от реального открытия страницы, включая загрузку HTML и скриптов).
 *
 * Для пользовательских фаз вида «блок реально появился у пользователя»:
 *   perfPoint('ui:first-field-ready')  — в момент, когда блок стал доступен.
 *
 * Отправляется в Sentry один раз на имя (повторные вызовы игнорируются).
 * Точки позже 30с (окно транзакции Sentry) не отправляются: это восстановленные
 * или ушедшие в фон вкладки, где watcher срабатывает через минуты — такие
 * значения (до 3 минут) не имеют отношения к скорости загрузки и отравляют p95.
 *
 * @param {string} name
 * @returns {number} мс от открытия страницы (0 — замер не отправлен)
 */
export function perfPoint(name) {
  if (reportedPoints.has(name)) return 0
  if (typeof performance === 'undefined' || !performance.now) return 0

  const at = performance.now()
  if (!(at > 0) || at > POINT_MAX_MS) return 0
  reportedPoints.add(name)

  const fullName = `${PREFIX}:${name}`
  if (performance.mark) {
    try {
      // метка для локального таймлайна showPerf(), время фиксируем то же,
      // что и ушло в Sentry
      performance.mark(fullName + '.point', { startTime: at })
    } catch {
      // performance.mark недоступен — пропускаем
    }
  }

  if (window.prod) {
    try {
      Sentry.setMeasurement(toSentryName(name), at, 'millisecond')
    } catch {
      // нет активной транзакции — тихо игнорируем
    }
  }

  return at
}

/**
 * Выводит сводный таймлайн всех замеров в консоль.
 * Читает данные напрямую из Performance API, поэтому видит всю историю,
 * даже если вызван позже.
 */
export function perfDump() {
  if (typeof performance === 'undefined' || !performance.getEntriesByType)
    return
  const measures = [
    ...performance
      .getEntriesByType('measure')
      .filter((m) => m.name.startsWith(PREFIX)),
    ...performance
      .getEntriesByType('mark')
      .filter((m) => m.name.startsWith(PREFIX) && m.name.endsWith('.point')),
  ].sort((a, b) => a.startTime - b.startTime)

  if (!measures.length) {
    console.log(`[${PREFIX}] замеров пока нет`)
    return
  }

  const t0 = measures[0].startTime
  console.group(
    `%c[${PREFIX}] таймлайн загрузки (${measures.length} замеров)`,
    'color: #726CE7; font-weight: bold; font-size: 13px'
  )
  console.log(
    '%c  СТАРТ        ДЛИТЕЛЬНОСТЬ      ЗАМЕР',
    'color: #726CE7; font-weight: bold; font-size: 11px'
  )
  for (const m of measures) {
    const offset = (m.startTime - t0).toFixed(0)
    const dur = m.duration.toFixed(0)
    const shortName = m.name
      .replace(PREFIX + ':', '')
      .replace('.measure', '')
      .replace('.point', '')
    const label = describe(shortName)
    const color =
      m.duration > 3000 ? '#ff585d' : m.duration > 1000 ? '#EDA12F' : '#4CAF50'
    console.log(
      `%c  +${offset.padStart(6)}мс    %c${dur.padStart(6)}мс      %c${shortName}%c — ${label}`,
      'color: #9e9e9e',
      `color: ${color}; font-weight: bold`,
      'color: #e0e0e0; font-weight: bold',
      'color: #9e9e9e'
    )
  }
  console.groupEnd()
}

// Регистрируем глобальный шорткат showPerf() для быстрого доступа из консоли
if (typeof window !== 'undefined') {
  window.showPerf = perfDump
}
